import { createContext, useContext } from 'react';

interface GlobalConfigProviderProps {
  children: React.ReactNode;
  config: GlobalConfig;
}

export interface GlobalConfig {
  /**
   * List of supported SSO domains (e.g. 'zeiss.com').
   *
   * @remarks
   * If user adds an email address with one of these domains during sign up or password reset,
   * user should be allowed to continue with SSO sign in flow.
   */
  emailDomainsForSSO?: string[] | null;
  /**
   * URL path for the hosted locations files (Blob Storage)
   */
  locationsApiUrl: string;
  /**
   * List of supported location ISO codes (uppercase, e.g. 'DE', 'EN').
   *
   * @remarks
   * ZID1.0 currently supports only a specific set of locations. This prop ensures
   * that users can only select country prefixes that are available in ZID1.0,
   * preventing selection of unsupported locations.
   */
  supportedLocations: string[];
}

// eslint-disable-next-line react-refresh/only-export-components
export const GlobalConfigContext = createContext<GlobalConfig | undefined>(
  undefined,
);

// eslint-disable-next-line react-refresh/only-export-components
export const useGlobalConfig = () => {
  const context = useContext(GlobalConfigContext);

  if (context === undefined) {
    throw new Error(
      `@zeiss/lib-components: global config not set. Use GlobalConfigProvider to set one.
      `,
    );
  }

  return context;
};

/**
 * Context provider to set up configuration for lib-components
 *
 */
export const GlobalConfigProvider = ({
  children,
  config,
}: GlobalConfigProviderProps) => {
  return (
    <GlobalConfigContext.Provider value={config}>
      {children}
    </GlobalConfigContext.Provider>
  );
};
